import { $fetch } from 'ofetch';
import { authStorage } from './auth-storage';
import { isPendingAuthToken } from '@shared/lib/jwt';
import { getOrCreateDeviceId } from '@shared/lib/device-id';
import { rememberSecurityNotice, isTerminalSessionCode } from '@shared/lib/security-notice';
import {
  ApiError,
  ValidationError,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
} from '../errors/errors';

export interface ApiClientConfig {
  baseURL: string;
}

export interface ApiRequestOptions {
  headers?: Record<string, string>;
}

let refreshTokenInProgress: Promise<{ accessToken: string } | null> | null = null;

interface HttpErrorBody {
  message?: string;
  errors?: Record<string, string[]> | unknown[];
}

interface HttpError {
  status?: number;
  data?: HttpErrorBody;
}

const asHttpError = (error: unknown): HttpError =>
  typeof error === 'object' && error !== null ? (error as HttpError) : {};

const readErrorCode = (error: unknown): string | undefined => {
  const errors = asHttpError(error).data?.errors;
  if (!Array.isArray(errors) || !errors[0] || typeof errors[0] !== 'object') {
    return undefined;
  }
  const code = (errors[0] as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
};

export function createApiClient(config: ApiClientConfig) {
  const baseClient = $fetch.create({
    baseURL: config.baseURL,
    credentials: 'include',
  });

  async function refreshAccessToken(): Promise<{ accessToken: string } | null> {
    if (refreshTokenInProgress) {
      return refreshTokenInProgress;
    }

    refreshTokenInProgress = (async () => {
      try {
        const response = await baseClient<{ token: string }>('/app/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Id': getOrCreateDeviceId(),
          },
        });

        if (!response?.token) {
          return null;
        }

        return { accessToken: response.token };
      } catch {
        return null;
      } finally {
        refreshTokenInProgress = null;
      }
    })();

    return refreshTokenInProgress;
  }

  function rejectTerminalSession(error: unknown): boolean {
    const code = readErrorCode(error);
    if (!isTerminalSessionCode(code)) {
      return false;
    }
    authStorage.clear();
    rememberSecurityNotice(code);
    return true;
  }

  async function getHeaders(accessToken?: string): Promise<Record<string, string>> {
    const tokens = authStorage.getTokens();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Device-Id': getOrCreateDeviceId(),
    };

    const bearer = accessToken ?? tokens?.accessToken;
    if (bearer) {
      headers.Authorization = `Bearer ${bearer}`;
    }

    return headers;
  }

  const mergeHeaders = (base: Record<string, string>, extra?: Record<string, string>): Record<string, string> =>
    extra ? { ...base, ...extra } : base;

  async function tryRefreshOnUnauthorized<T>(
    retry: (headers: Record<string, string>) => Promise<T>,
  ): Promise<T> {
    const accessToken = authStorage.getAccessToken();
    if (!accessToken || isPendingAuthToken(accessToken)) {
      throw new UnauthorizedError();
    }

    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw new UnauthorizedError();
    }

    authStorage.setAccessToken(refreshed.accessToken);
    const newHeaders = await getHeaders();
    return retry(newHeaders);
  }

  const apiClient = {
    async get<T>(url: string): Promise<T> {
      const headers = await getHeaders();
      try {
        const response = await baseClient<T>(url, {
          method: 'GET',
          headers,
          credentials: 'include',
        });
        return response;
      } catch (error: unknown) {
        if (asHttpError(error).status === 401) {
          if (rejectTerminalSession(error)) {
            throw new UnauthorizedError();
          }
          return tryRefreshOnUnauthorized((newHeaders) =>
            baseClient<T>(url, {
              method: 'GET',
              headers: newHeaders,
              credentials: 'include',
            }),
          );
        }
        throw apiClient.handleError(error);
      }
    },

    async post<T>(url: string, body: unknown = {}, options?: ApiRequestOptions): Promise<T> {
      const headers = mergeHeaders(await getHeaders(), options?.headers);
      try {
        const response = await baseClient<T>(url, {
          method: 'POST',
          body: body || {},
          headers,
          credentials: 'include',
        });
        return response;
      } catch (error: unknown) {
        if (asHttpError(error).status === 401) {
          if (rejectTerminalSession(error)) {
            throw new UnauthorizedError();
          }
          return tryRefreshOnUnauthorized((newHeaders) =>
            baseClient<T>(url, {
              method: 'POST',
              body: body || {},
              headers: mergeHeaders(newHeaders, options?.headers),
              credentials: 'include',
            }),
          );
        }
        throw apiClient.handleError(error);
      }
    },

    async patch<T>(url: string, body: unknown = {}, options?: ApiRequestOptions): Promise<T> {
      const headers = mergeHeaders(await getHeaders(), options?.headers);
      try {
        const response = await baseClient<T>(url, {
          method: 'PATCH',
          body: body || {},
          headers,
          credentials: 'include',
        });
        return response;
      } catch (error: unknown) {
        if (asHttpError(error).status === 401) {
          if (rejectTerminalSession(error)) {
            throw new UnauthorizedError();
          }
          return tryRefreshOnUnauthorized((newHeaders) =>
            baseClient<T>(url, {
              method: 'PATCH',
              body: body || {},
              headers: mergeHeaders(newHeaders, options?.headers),
              credentials: 'include',
            }),
          );
        }
        throw apiClient.handleError(error);
      }
    },

    async put<T>(url: string, body: unknown = {}): Promise<T> {
      const headers = await getHeaders();
      try {
        const response = await baseClient<T>(url, {
          method: 'PUT',
          body: body || {},
          headers,
          credentials: 'include',
        });
        return response;
      } catch (error: unknown) {
        if (asHttpError(error).status === 401) {
          if (rejectTerminalSession(error)) {
            throw new UnauthorizedError();
          }
          return tryRefreshOnUnauthorized((newHeaders) =>
            baseClient<T>(url, {
              method: 'PUT',
              body: body || {},
              headers: newHeaders,
              credentials: 'include',
            }),
          );
        }
        throw apiClient.handleError(error);
      }
    },

    async getBlob(url: string): Promise<{ blob: Blob; headers: Headers }> {
      const headers = await getHeaders();
      const request = (nextHeaders: Record<string, string>) =>
        baseClient.raw(url, {
          method: 'GET',
          headers: nextHeaders,
          credentials: 'include',
          responseType: 'blob',
        });

      try {
        const response = await request(headers);
        return { blob: response._data as Blob, headers: response.headers };
      } catch (error: unknown) {
        if (asHttpError(error).status === 401) {
          if (rejectTerminalSession(error)) {
            throw new UnauthorizedError();
          }
          return tryRefreshOnUnauthorized(async (nextHeaders) => {
            const response = await request(nextHeaders);
            return { blob: response._data as Blob, headers: response.headers };
          });
        }
        throw await apiClient.handleBlobError(error);
      }
    },

    async delete<T>(url: string, body?: unknown): Promise<T> {
      const headers = await getHeaders();
      try {
        const response = await baseClient<T>(url, {
          method: 'DELETE',
          ...(body !== undefined ? { body } : {}),
          headers,
          credentials: 'include',
        });
        return response;
      } catch (error: unknown) {
        if (asHttpError(error).status === 401) {
          if (rejectTerminalSession(error)) {
            throw new UnauthorizedError();
          }
          return tryRefreshOnUnauthorized((newHeaders) =>
            baseClient<T>(url, {
              method: 'DELETE',
              ...(body !== undefined ? { body } : {}),
              headers: newHeaders,
              credentials: 'include',
            }),
          );
        }
        throw apiClient.handleError(error);
      }
    },

    async postWithAccessToken<T>(url: string, body: unknown, accessToken: string): Promise<T> {
      const headers = await getHeaders(accessToken);
      try {
        return await baseClient<T>(url, {
          method: 'POST',
          body: body || {},
          headers,
          credentials: 'include',
        });
      } catch (error: unknown) {
        throw apiClient.handleError(error);
      }
    },

    async handleBlobError(error: unknown): Promise<Error> {
      const http = asHttpError(error);
      const data = http.data as unknown;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text) as HttpErrorBody;
          return apiClient.handleError({ status: http.status, data: json });
        } catch {
          return apiClient.handleError({ status: http.status, data: undefined });
        }
      }
      return apiClient.handleError(error);
    },

    handleError(error: unknown): Error {
      const { status, data } = asHttpError(error);

      switch (status) {
        case 400:
          if (data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
            return new ValidationError(data.errors, data.message || 'Validation error');
          }
          return new ApiError(data?.message || 'Bad request', status, data);

        case 401:
          return new UnauthorizedError(data?.message || 'Unauthorized');

        case 403:
          return new ForbiddenError(data?.message || 'Forbidden');

        case 404:
          return new NotFoundError(data?.message || 'Not found');

        default:
          return new ApiError(
            data?.message || 'An error occurred',
            status,
            data
          );
      }
    },
  };

  return apiClient;
}

export type ApiClient = ReturnType<typeof createApiClient>;
